//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { IScheduledInstagramPost } from '@apis/universal-posts/scheduled-posts/universalScheduledPostsApiModel';
import { IGroupedScheduledPost } from '@instagram-account/redux/schedule/selectors/getScheduledPostsGroupedByDay';
import DateString from '@models/DateString';
import { DndItemType } from '@models/DndItemType';
import IUserSettings from '@models/IUserSettings';
import { InstagramPostType } from '@models/InstagramPostType';
import PostableId from '@models/PostableId';
import { UniversalCalendarView } from '@universal-scheduling/redux/calendar/universalCalendarModel';
import { WeekDayValue } from '@utils/moment';
import { Normalized } from '@utils/normalize';

export interface IInstagramScheduleState {
  errors: {
    fetchInteractiveCalendarSettingsError?: string;
    updateInteractiveCalendarSettingsError?: string;
    fetchScheduledPostsError?: string;
    reschedulePostError?: string;
  };
  flags: {
    /**
     * This flag indicates whether a create post ActionSheet or calendar item DetailsSheet is open
     * within the week view, and is used to ensure users can close sheets on outside click without
     * accidentally opening a new one on the same click.
     */
    isSheetOpen: boolean;
    isFetchingInteractiveCalendarSettings: boolean;
    isUpdatingInteractiveCalendarSettings: boolean;
    isFetchingScheduledPosts: boolean;
    isInitialScheduleDataLoaded: boolean;
    isReschedulingPost: boolean;
  };
  startOfWeekDay: WeekDayValue;
  scheduledPosts: NormalizedScheduledPosts;
  startDate: DateString;
  view: UniversalCalendarView;
  postFilter: InstagramSchedulePostFilter;
  captionSearchText?: string;

  /**
   * Start date to return the calendar to following a failed drag & drop
   * operation that navigated away to a different time period.
   */
  navigationReturnDate?: DateString;
  /**
   * The current state of the drag operation's navigation. Will be 'forward'
   * if the user is hovering outside of the calendar to the right, 'backward'
   * if the user is hovering outside of the calendar to the left, and 'none'
   * at all other times.
   */
  navigationState: InstagramScheduleNavigationState;
}

// Shorthand type for usage in the schedule redux infrastructure.
export type NormalizedScheduledPosts = Normalized<
  IScheduledInstagramPost,
  PostableId
>;

export type InstagramSchedulePostFilter =
  | 'all'
  | InstagramPostType.Post
  | InstagramPostType.Story
  | InstagramPostType.Reels;

/**
 * The state of edge scrolling navigation during a drag operation, used to facilitate
 * navigation on an interval while a user is hovering an item outside the right
 * and left bounds of the calendar.
 */
export type InstagramScheduleNavigationState = 'forward' | 'backward' | 'none';

/**
 * The day that should represent the start of the week, as
 * determined by Moment's day values where 0 = Sunday.
 * https://momentjs.com/docs/#/get-set/day/
 */
export const START_OF_WEEK_DAY = 0;

/**
 * The offset that using the forward/backward navigations should apply
 * to the startDate for each view. If we are moving forward, we will add this
 * offset to the startDate. If we are moving backwards, we will subtract it.
 */
export const InstagramScheduleDateChangeOffset = {
  [UniversalCalendarView.Week]: 7,
  [UniversalCalendarView.Timeline]: 1,
};

/**
 * Models for the week view drag and drop components. All calendar items
 * are tied into the drag and drop system regardless of whether they are
 * eligible to be dragged or not.
 */

export interface IWeekViewItemTime {
  // number between 0-6 --> day offset relative to the startDate
  dayOffset: number;
  // number between 0-1439 --> minute of the day relative to midnight
  minuteOfDay: number;
}

export interface IWeekViewCalendarItem {
  id: PostableId;
  time: IWeekViewItemTime;
  isStory: boolean;
  // The index of this post within its post group.
  postGroupIndex: number;
}

export interface IWeekViewCalendarItemWithType extends IWeekViewCalendarItem {
  type: typeof DndItemType.InstagramScheduleWeekViewItem;
}

export interface IMonthViewCalendarItem extends IGroupedScheduledPost {
  type: typeof DndItemType.InstagramScheduleMonthViewItem;
}

/**
 * Models for the timeline view.
 */

export interface ITimelineViewPostGroup {
  date: DateString;
  items: IGroupedScheduledPost[];
}

export type InteractiveCalendarSettings = Pick<
  IUserSettings,
  'scheduleCalendarStartOfWeek' | 'scheduleCalendarView'
>;

export const SCHEDULE_FETCH_LIMIT = 80;
